/* eslint-disable */
// Staff Leave Management — two self-contained components:
//   Admin:  <StaffLeaveAdmin staff={[...]} />
//   Staff:  <StaffLeaveRequest staffId="uuid" staffName="Name" />
// Requires tables: staff_leave_requests, staff_leave_balance (see SQL in chat)
import React from "react";
import { supabase } from "./supabaseClient";
import { CoverAssignmentModal } from "./CoverDuty";

const C = {
  primary: "#1a6b3c", primaryPale: "#e8f5ee",
  rose: "#e11d48", rosePale: "#fff0f3",
  amber: "#d97706", amberPale: "#fffbeb",
  blue: "#2563eb", bluePale: "#eff6ff",
  purple: "#7c3aed", purplePale: "#f5f3ff",
  text: "#111827", muted: "#6b7280", border: "#d1fae5",
  white: "#ffffff", bg: "#f9fafb",
};

const LEAVE_TYPES = ["Casual", "Sick", "Annual", "Emergency"];
const TYPE_COLOR  = { Casual: C.blue, Sick: C.purple, Annual: C.primary, Emergency: C.rose };
const STATUS_STYLE = {
  pending:  { bg: "#fffbeb", fg: "#d97706", bd: "#fde68a" },
  approved: { bg: "#f0fdf4", fg: "#16a34a", bd: "#bbf7d0" },
  rejected: { bg: "#fef2f2", fg: "#dc2626", bd: "#fecaca" },
};

const YEAR = String(new Date().getFullYear());

const Badge = ({ s }) => {
  const c = STATUS_STYLE[s] || STATUS_STYLE.pending;
  return (
    <span style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, borderRadius: 8, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
      {s ? s.charAt(0).toUpperCase() + s.slice(1) : "Pending"}
    </span>
  );
};

const fmt = d => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const daysBetween = (a, b) => (a && b) ? Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1) : 0;

async function getOrCreateBalance(staffId) {
  const { data } = await supabase
    .from("staff_leave_balance")
    .select("*")
    .eq("staff_id", staffId)
    .eq("year", YEAR)
    .maybeSingle();
  if (data) return data;
  const defaults = {
    staff_id: staffId, year: YEAR,
    casual_total: 10, sick_total: 10, annual_total: 14,
    casual_used: 0,   sick_used: 0,   annual_used: 0, emergency_used: 0,
  };
  const { data: inserted } = await supabase
    .from("staff_leave_balance")
    .insert(defaults)
    .select()
    .single();
  return inserted || defaults;
}

async function deductLeave(staffId, leaveType, days) {
  const col = leaveType.toLowerCase() + "_used";
  const { data: bal } = await supabase
    .from("staff_leave_balance")
    .select("*")
    .eq("staff_id", staffId)
    .eq("year", YEAR)
    .maybeSingle();
  if (!bal) return;
  await supabase
    .from("staff_leave_balance")
    .update({ [col]: (bal[col] || 0) + days })
    .eq("id", bal.id);
}

// ── StaffLeaveAdmin ──────────────────────────────────────────────────────────
export function StaffLeaveAdmin({ staff = [] }) {
  const [rows,       setRows]       = React.useState([]);
  const [balances,   setBalances]   = React.useState([]);
  const [filter,     setFilter]     = React.useState("pending");
  const [view,       setView]       = React.useState("requests"); // "requests" | "balances"
  const [loading,    setLoading]    = React.useState(true);
  const [me,         setMe]         = React.useState("Admin");
  const [editBalId,  setEditBalId]  = React.useState(null);
  const [editBalForm,setEditBalForm]= React.useState({ casual_total: 10, sick_total: 10, annual_total: 14 });
  const [savingBal,  setSavingBal]  = React.useState(false);
  const [coverModal, setCoverModal] = React.useState(null); // leave request that just got approved

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    setMe(user?.email || "Admin");
    const [lr, lb] = await Promise.all([
      supabase.from("staff_leave_requests").select("*").order("created_at", { ascending: false }),
      supabase.from("staff_leave_balance").select("*").eq("year", YEAR),
    ]);
    setRows(lr.data || []);
    setBalances(lb.data || []);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const decide = async (r, status) => {
    const days = daysBetween(r.from_date, r.to_date);
    if (status === "approved") {
      if (!window.confirm(`Approve ${days} day(s) of ${r.leave_type || "Casual"} leave for ${r.staff_name}?\nThis will deduct from their leave balance.`)) return;
    }
    const note = status === "rejected" ? (window.prompt("Optional note for staff (optional — press Cancel to skip):") || "") : "";
    const { error } = await supabase.from("staff_leave_requests").update({
      status,
      admin_note:  note,
      decided_at:  new Date().toISOString(),
      decided_by:  me,
    }).eq("id", r.id);
    if (error) return alert("Error: " + error.message);
    if (status === "approved") {
      await deductLeave(r.staff_id, r.leave_type || "Casual", days);
      // Open cover assignment modal immediately after approval
      setCoverModal({ ...r, status: "approved" });
    }
    // Notify the specific teacher via their email (used as target_class for personal routing)
    const staffMember = staff.find(s => s.id === r.staff_id);
    const staffEmail  = staffMember?.email || null;
    supabase.from("notifications").insert({
      title: status === "approved" ? "✅ Leave Approved" : "❌ Leave Not Approved",
      body: `Your ${r.leave_type || "Casual"} leave (${fmt(r.from_date)} – ${fmt(r.to_date)}) was ${status}${note ? ": " + note : "."}`,
      type: status === "approved" ? "general" : "alert",
      target_role: "teachers",
      target_class: staffEmail,
      source_type: "staff_leave",
      created_by: "Admin",
    });
    setRows(p => p.map(x => x.id === r.id ? { ...x, status, admin_note: note } : x));
    if (status !== "approved") load(); // approved path reloads after modal closes
  };

  const pending  = rows.filter(r => r.status === "pending").length;
  const shown    = filter === "all" ? rows : rows.filter(r => r.status === filter);
  const getBal   = id => balances.find(b => b.staff_id === id);
  const activeStaff = staff.filter(s => s.status === "Active");

  const openEditBal = (s) => {
    const b = getBal(s.id) || { casual_total: 10, sick_total: 10, annual_total: 14 };
    setEditBalForm({ casual_total: b.casual_total, sick_total: b.sick_total, annual_total: b.annual_total });
    setEditBalId(s.id);
  };

  const saveBalance = async (staffId) => {
    setSavingBal(true);
    const existing = getBal(staffId);
    const payload  = {
      staff_id:     staffId,
      year:         YEAR,
      casual_total: Number(editBalForm.casual_total)  || 0,
      sick_total:   Number(editBalForm.sick_total)    || 0,
      annual_total: Number(editBalForm.annual_total)  || 0,
    };
    let error;
    if (existing) {
      ({ error } = await supabase.from("staff_leave_balance").update(payload).eq("id", existing.id));
    } else {
      ({ error } = await supabase.from("staff_leave_balance").insert({ ...payload, casual_used: 0, sick_used: 0, annual_used: 0, emergency_used: 0 }));
    }
    setSavingBal(false);
    if (error) { alert("Error saving: " + error.message); return; }
    setEditBalId(null);
    load();
  };

  const inp = { padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, background: C.white };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ background: C.white, borderRadius: 14, padding: "16px 20px", border: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: C.primary }}>📝 Staff Leave Management</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Review requests and monitor leave balances</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setView("requests")}
              style={{ background: view === "requests" ? C.primary : C.white, color: view === "requests" ? C.white : C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              Requests {pending > 0 && <span style={{ background: C.amber, color: C.white, borderRadius: 20, padding: "0 7px", marginLeft: 4, fontSize: 11 }}>{pending}</span>}
            </button>
            <button onClick={() => setView("balances")}
              style={{ background: view === "balances" ? C.primary : C.white, color: view === "balances" ? C.white : C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              Leave Balances
            </button>
          </div>
        </div>
      </div>

      {/* Requests */}
      {view === "requests" && (
        <div style={{ background: C.white, borderRadius: 14, padding: "16px 20px", border: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {["pending", "approved", "rejected", "all"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ background: filter === f ? C.primary : C.bg, color: filter === f ? C.white : C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {loading && <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>}
          {!loading && shown.length === 0 && (
            <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "24px 0" }}>
              No {filter !== "all" ? filter : ""} requests.
            </div>
          )}

          {shown.map(r => {
            const days = daysBetween(r.from_date, r.to_date);
            const tc   = TYPE_COLOR[r.leave_type] || C.muted;
            return (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "14px 0", borderTop: "1px solid #f3f4f6", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${tc}18`, display: "flex", alignItems: "center", justifyContent: "center", color: tc, fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                      {r.staff_name?.[0] || "?"}
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{r.staff_name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>Applied: {fmt(r.created_at)}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ background: `${tc}15`, color: tc, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{r.leave_type || "Casual"}</span>
                    <span style={{ background: "#f3f4f6", color: C.text,   borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{days} day{days !== 1 ? "s" : ""}</span>
                    <span style={{ background: "#f3f4f6", color: C.muted,  borderRadius: 6, padding: "2px 8px", fontSize: 11 }}>{fmt(r.from_date)} → {fmt(r.to_date)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted }}>{r.reason || "No reason given."}</div>
                  {r.admin_note && <div style={{ fontSize: 11, color: C.purple, marginTop: 4 }}>Admin note: {r.admin_note}</div>}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                  {r.status === "pending" ? (
                    <>
                      <button onClick={() => decide(r, "approved")} style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Approve</button>
                      <button onClick={() => decide(r, "rejected")} style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Reject</button>
                    </>
                  ) : <Badge s={r.status} />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Balances */}
      {view === "balances" && (
        <div style={{ background: C.white, borderRadius: 14, padding: "16px 20px", border: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Leave Quotas & Balances — {YEAR}</div>
            <div style={{ fontSize: 11, color: C.muted }}>Click "Edit Quota" to change the total days allowed per staff member</div>
          </div>
          {activeStaff.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>No active staff found.</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {activeStaff.map(s => {
              const b     = getBal(s.id) || { casual_total: 10, sick_total: 10, annual_total: 14, casual_used: 0, sick_used: 0, annual_used: 0 };
              const types = [
                { label: "Casual", total: b.casual_total, used: b.casual_used, color: C.blue,    key: "casual_total" },
                { label: "Sick",   total: b.sick_total,   used: b.sick_used,   color: C.purple,  key: "sick_total" },
                { label: "Annual", total: b.annual_total, used: b.annual_used, color: C.primary, key: "annual_total" },
              ];
              const isEditing = editBalId === s.id;
              return (
                <div key={s.id} style={{ background: C.bg, borderRadius: 12, padding: "14px 16px", border: `1px solid ${isEditing ? C.primary : C.border}`, transition: "border-color .2s" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div>
                      <span style={{ fontWeight: 800, fontSize: 13, color: C.text }}>{s.name}</span>
                      <span style={{ fontWeight: 400, fontSize: 11, color: C.muted }}> · {s.role}</span>
                    </div>
                    {!isEditing
                      ? <button onClick={() => openEditBal(s)} style={{ background: C.primaryPale, color: C.primary, border: "none", borderRadius: 7, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✏️ Edit Quota</button>
                      : <button onClick={() => setEditBalId(null)} style={{ background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 7, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕ Cancel</button>
                    }
                  </div>

                  {/* Edit form */}
                  {isEditing && (
                    <div style={{ background: C.white, borderRadius: 10, padding: "12px 14px", marginBottom: 12, border: `1px solid ${C.primaryMid}` }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.primary, marginBottom: 8 }}>Set yearly quota (total days allowed):</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                        {[["Casual", "casual_total", C.blue], ["Sick", "sick_total", C.purple], ["Annual", "annual_total", C.primary]].map(([lbl, key, color]) => (
                          <div key={key}>
                            <label style={{ fontSize: 10, fontWeight: 700, color, display: "block", marginBottom: 3 }}>{lbl}</label>
                            <input type="number" min="0" max="365" value={editBalForm[key]}
                              onChange={e => setEditBalForm(p => ({ ...p, [key]: e.target.value }))}
                              style={{ width: "100%", padding: "6px 8px", border: `1.5px solid ${color}40`, borderRadius: 7, fontSize: 13, fontWeight: 700, color, textAlign: "center", boxSizing: "border-box", background: C.white }} />
                          </div>
                        ))}
                      </div>
                      <button onClick={() => saveBalance(s.id)} disabled={savingBal}
                        style={{ width: "100%", background: savingBal ? "#9ca3af" : C.primary, color: C.white, border: "none", borderRadius: 8, padding: "7px", fontWeight: 700, fontSize: 12, cursor: savingBal ? "default" : "pointer" }}>
                        {savingBal ? "Saving…" : "💾 Save Quota"}
                      </button>
                    </div>
                  )}

                  {/* Balance bars */}
                  {types.map(t => {
                    const rem = Math.max(0, t.total - t.used);
                    const pct = t.total > 0 ? Math.min(100, (t.used / t.total) * 100) : 0;
                    return (
                      <div key={t.label} style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 600, marginBottom: 3 }}>
                          <span style={{ color: t.color }}>{t.label}</span>
                          <span style={{ color: C.muted }}>{rem}/{t.total} left · used: {t.used}</span>
                        </div>
                        <div style={{ height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: t.color, borderRadius: 3, transition: "width .4s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cover assignment modal — auto-opens after leave approval */}
      {coverModal && (
        <CoverAssignmentModal
          leaveRequest={coverModal}
          staff={staff.filter(s => s.status === "Active")}
          onClose={() => { setCoverModal(null); load(); }}
          onSaved={() => { setCoverModal(null); load(); }}
        />
      )}
    </div>
  );
}

// ── StaffLeaveRequest (Teacher Self-Service) ─────────────────────────────────
export function StaffLeaveRequest({ staffId, staffName }) {
  const [form,    setForm]    = React.useState({ from_date: "", to_date: "", leave_type: "Casual", reason: "" });
  const [rows,    setRows]    = React.useState([]);
  const [balance, setBalance] = React.useState(null);
  const [busy,    setBusy]    = React.useState(false);
  const [msg,     setMsg]     = React.useState(null);

  const load = React.useCallback(async () => {
    if (!staffId) return;
    const [lr, bal] = await Promise.all([
      supabase.from("staff_leave_requests").select("*").eq("staff_id", staffId).order("created_at", { ascending: false }),
      getOrCreateBalance(staffId),
    ]);
    setRows(lr.data || []);
    setBalance(bal);
  }, [staffId]);

  React.useEffect(() => { load(); }, [load]);

  const days      = form.from_date && form.to_date ? daysBetween(form.from_date, form.to_date) : 0;
  const remaining = (() => {
    if (!balance) return null;
    const t = form.leave_type.toLowerCase();
    if (t === "emergency") return null;
    return Math.max(0, (balance[t + "_total"] || 0) - (balance[t + "_used"] || 0));
  })();
  const overLimit = remaining !== null && days > remaining;

  const submit = async () => {
    if (!staffId) return setMsg({ ok: false, t: "Your account is not linked to a staff record. Ask your admin to link it." });
    if (!form.from_date || !form.to_date) return setMsg({ ok: false, t: "Please select both dates." });
    if (form.to_date < form.from_date)    return setMsg({ ok: false, t: "End date cannot be before start date." });
    if (overLimit) return setMsg({ ok: false, t: `Only ${remaining} ${form.leave_type} days remaining.` });
    setBusy(true);
    const { error } = await supabase.from("staff_leave_requests").insert({
      staff_id:   staffId,
      staff_name: staffName || "",
      from_date:  form.from_date,
      to_date:    form.to_date,
      leave_type: form.leave_type,
      reason:     form.reason || "",
      status:     "pending",
    });
    setBusy(false);
    if (error) return setMsg({ ok: false, t: error.message });
    // Notify admins of new request
    supabase.from("notifications").insert({
      title: `📝 Leave Request: ${staffName || "Staff"}`,
      body: `${form.leave_type} leave from ${form.from_date} to ${form.to_date}${form.reason ? " — " + form.reason.slice(0, 80) : ""}`,
      type: "alert",
      target_role: "admin",
      source_type: "staff_leave",
      created_by: staffName || "Staff",
    });
    setMsg({ ok: true, t: "Leave request submitted. Awaiting approval." });
    setForm({ from_date: "", to_date: "", leave_type: "Casual", reason: "" });
    load();
  };

  const inp = { width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, boxSizing: "border-box", background: C.white };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Balance cards */}
      {balance && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 18px" }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: C.primary, marginBottom: 12 }}>📊 My Leave Balance — {YEAR}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {[["Casual", balance.casual_total, balance.casual_used, C.blue], ["Sick", balance.sick_total, balance.sick_used, C.purple], ["Annual", balance.annual_total, balance.annual_used, C.primary]].map(([lbl, total, used, color]) => {
              const rem = Math.max(0, total - used);
              const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
              return (
                <div key={lbl} style={{ background: C.bg, borderRadius: 10, padding: "12px 14px", border: `1px solid ${C.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 4 }}>{lbl} Leave</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color, lineHeight: 1 }}>
                    {rem}<span style={{ fontSize: 12, fontWeight: 400, color: C.muted }}>/{total}</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>days remaining</div>
                  <div style={{ height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 2 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No staff link warning */}
      {!staffId && (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: "12px 16px", fontSize: 13, color: "#92400e", fontWeight: 600 }}>
          ⚠️ Your teacher account is not linked to a staff record. Ask your administrator to link your account so you can submit leave requests.
        </div>
      )}

      {/* Request form */}
      {staffId && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: C.primary, marginBottom: 14 }}>📝 Apply for Leave</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>From</label>
              <input type="date" value={form.from_date} onChange={e => setForm(p => ({ ...p, from_date: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>To</label>
              <input type="date" value={form.to_date} onChange={e => setForm(p => ({ ...p, to_date: e.target.value }))} style={inp} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>Leave Type</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {LEAVE_TYPES.map(t => (
                <button key={t} onClick={() => setForm(p => ({ ...p, leave_type: t }))}
                  style={{ background: form.leave_type === t ? (TYPE_COLOR[t] || C.primary) : C.bg, color: form.leave_type === t ? C.white : C.text, border: `1px solid ${form.leave_type === t ? (TYPE_COLOR[t] || C.primary) : C.border}`, borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>Reason</label>
            <textarea rows={2} value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="Briefly describe the reason for leave…" style={{ ...inp, resize: "vertical" }} />
          </div>

          {days > 0 && (
            <div style={{ background: overLimit ? "#fef2f2" : "#f0fdf4", border: `1px solid ${overLimit ? "#fecaca" : "#bbf7d0"}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: overLimit ? "#dc2626" : "#16a34a", fontWeight: 600, marginBottom: 10 }}>
              {days} day{days !== 1 ? "s" : ""} requested
              {remaining !== null ? ` · ${remaining} ${form.leave_type.toLowerCase()} days remaining` : " · Emergency (no quota)"}
              {overLimit ? " — Insufficient balance!" : ""}
            </div>
          )}

          {msg && <div style={{ fontSize: 12, color: msg.ok ? "#16a34a" : "#dc2626", marginBottom: 8 }}>{msg.t}</div>}
          <button onClick={submit} disabled={busy}
            style={{ width: "100%", background: busy ? "#9ca3af" : C.primary, color: C.white, border: "none", borderRadius: 9, padding: 10, fontWeight: 700, fontSize: 13, cursor: busy ? "default" : "pointer" }}>
            {busy ? "Submitting…" : "Submit Leave Request"}
          </button>
        </div>
      )}

      {/* History */}
      {rows.length > 0 && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 18px" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 10 }}>My Request History</div>
          {rows.map(r => {
            const d  = daysBetween(r.from_date, r.to_date);
            const tc = TYPE_COLOR[r.leave_type] || C.muted;
            return (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid #f3f4f6", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
                    <span style={{ background: `${tc}18`, color: tc, borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>{r.leave_type || "Casual"}</span>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{fmt(r.from_date)} → {fmt(r.to_date)}</span>
                    <span style={{ fontSize: 11, color: C.muted }}>({d}d)</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {r.reason || "—"}{r.admin_note ? ` · Admin: ${r.admin_note}` : ""}
                  </div>
                </div>
                <Badge s={r.status} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
